import type { ApiCommerceChainJourney } from "@bridge-applications/pb-types";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    CHAIN_OFFER_TIMING_MS,
    chainOfferWindowStart,
    type ChainOfferBoardPhase,
    type ChainOfferStep,
    visibleChainOfferSteps,
} from "../helpers/commerceChainPresentation";

interface ChainOfferPresentationState {
    journey: ApiCommerceChainJourney | null;
    phase: ChainOfferBoardPhase;
    incomingStep: ChainOfferStep | null;
}

interface ChainOfferPresentation {
    journey: ApiCommerceChainJourney | null;
    visibleSteps: ChainOfferStep[];
    phase: ChainOfferBoardPhase;
    incomingStep: ChainOfferStep | null;
    beginAuthoritativeAction: () => void;
    cancelAuthoritativeAction: () => void;
}

const REDUCED_MOTION_STEP_MS = 80;

function presentationDelay(delayMs: number): number {
    if (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        return REDUCED_MOTION_STEP_MS;
    }

    return delayMs;
}

function initialPresentationState(
    journey: ApiCommerceChainJourney | null,
): ChainOfferPresentationState {
    return {
        journey,
        phase: journey?.status === "COMPLETED" ? "complete" : "idle",
        incomingStep: null,
    };
}

export function useChainOfferPresentation(
    journey: ApiCommerceChainJourney | null,
    isOpen: boolean,
): ChainOfferPresentation {
    const [presentation, setPresentation] =
        useState<ChainOfferPresentationState>(() =>
            initialPresentationState(journey),
        );
    const presentationRef = useRef(presentation);
    const pendingJourneyVersionRef = useRef<string | null>(null);
    const timerIdsRef = useRef<number[]>([]);

    const updatePresentation = useCallback(
        (nextPresentation: ChainOfferPresentationState): void => {
            presentationRef.current = nextPresentation;
            setPresentation(nextPresentation);
        },
        [],
    );

    const clearTimers = useCallback((): void => {
        for (const timerId of timerIdsRef.current) {
            window.clearTimeout(timerId);
        }
        timerIdsRef.current = [];
    }, []);

    const schedule = useCallback(
        (callback: () => void, delayMs: number): void => {
            timerIdsRef.current.push(window.setTimeout(callback, delayMs));
        },
        [],
    );

    const synchronizeImmediately = useCallback(
        (nextJourney: ApiCommerceChainJourney | null): void => {
            clearTimers();
            pendingJourneyVersionRef.current = null;
            updatePresentation(initialPresentationState(nextJourney));
        },
        [clearTimers, updatePresentation],
    );

    const reconcileAuthoritativeJourney = useCallback(
        (nextJourney: ApiCommerceChainJourney): void => {
            const previousJourney = presentationRef.current.journey;
            if (previousJourney === null) {
                synchronizeImmediately(nextJourney);
                return;
            }

            clearTimers();
            pendingJourneyVersionRef.current = null;
            updatePresentation({
                journey: previousJourney,
                phase: "completing",
                incomingStep: null,
            });

            schedule(() => {
                if (nextJourney.status === "COMPLETED") {
                    updatePresentation({
                        journey: nextJourney,
                        phase: "complete",
                        incomingStep: null,
                    });
                    return;
                }

                const previousStart = chainOfferWindowStart(previousJourney);
                const nextStart = chainOfferWindowStart(nextJourney);
                if (nextStart > previousStart) {
                    const nextVisibleSteps =
                        visibleChainOfferSteps(nextJourney);
                    const incomingStep =
                        nextVisibleSteps[nextVisibleSteps.length - 1] ?? null;
                    updatePresentation({
                        journey: previousJourney,
                        phase: "advancing",
                        incomingStep,
                    });

                    schedule(() => {
                        updatePresentation({
                            journey: nextJourney,
                            phase: "unlocking",
                            incomingStep: null,
                        });
                        schedule(() => {
                            updatePresentation({
                                journey: nextJourney,
                                phase: "idle",
                                incomingStep: null,
                            });
                        }, presentationDelay(CHAIN_OFFER_TIMING_MS.unlock));
                    }, presentationDelay(CHAIN_OFFER_TIMING_MS.advance));
                    return;
                }

                updatePresentation({
                    journey: nextJourney,
                    phase: "unlocking",
                    incomingStep: null,
                });
                schedule(() => {
                    updatePresentation({
                        journey: nextJourney,
                        phase: "idle",
                        incomingStep: null,
                    });
                }, presentationDelay(CHAIN_OFFER_TIMING_MS.unlock));
            }, presentationDelay(CHAIN_OFFER_TIMING_MS.completionMark));
        },
        [clearTimers, schedule, synchronizeImmediately, updatePresentation],
    );

    useEffect(() => {
        if (!isOpen) {
            synchronizeImmediately(journey);
            return;
        }

        if (journey === null) return;

        const presentedJourney = presentationRef.current.journey;
        if (
            presentedJourney === null ||
            presentedJourney.journeyId !== journey.journeyId
        ) {
            synchronizeImmediately(journey);
            return;
        }

        if (presentedJourney.version === journey.version) return;

        if (
            pendingJourneyVersionRef.current === presentedJourney.version &&
            (journey.currentStepOrdinal > presentedJourney.currentStepOrdinal ||
                journey.status === "COMPLETED")
        ) {
            reconcileAuthoritativeJourney(journey);
            return;
        }

        synchronizeImmediately(journey);
    }, [
        isOpen,
        journey,
        reconcileAuthoritativeJourney,
        synchronizeImmediately,
    ]);

    useEffect(
        () => () => {
            clearTimers();
        },
        [clearTimers],
    );

    const beginAuthoritativeAction = useCallback((): void => {
        pendingJourneyVersionRef.current =
            presentationRef.current.journey?.version ?? null;
    }, []);

    const cancelAuthoritativeAction = useCallback((): void => {
        pendingJourneyVersionRef.current = null;
    }, []);

    return {
        journey: presentation.journey,
        visibleSteps:
            presentation.journey === null
                ? []
                : visibleChainOfferSteps(presentation.journey),
        phase: presentation.phase,
        incomingStep: presentation.incomingStep,
        beginAuthoritativeAction,
        cancelAuthoritativeAction,
    };
}
